import { useEffect, useState } from 'react';
import { Tag, AlertTriangle } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';

interface CategorizarLoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  produtos: { id: string; nome: string; categoria?: string | null }[];
  categorias: string[];
  onAplicado: (mensagem: string) => void;
}

export function CategorizarLoteModal({
  isOpen,
  onClose,
  produtos,
  categorias,
  onAplicado,
}: CategorizarLoteModalProps) {
  const [categoria, setCategoria] = useState('');
  const utils = trpc.useUtils();

  const mutation = trpc.produtos.atualizarCategoriaEmLote.useMutation({
    onSuccess: (resultado) => {
      utils.produtos.list.invalidate();
      utils.produtos.listCategorias.invalidate();
      onAplicado(resultado.message ?? 'Categoria aplicada');
      onClose();
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    setCategoria('');
    mutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const limpando = categoria.trim() === '';
  const jaCategorizados = produtos.filter((p) => p.categoria).length;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Categorizar em lote">
      <div className="space-y-4">
        <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
          {produtos.length}{' '}
          {produtos.length === 1 ? 'produto selecionado' : 'produtos selecionados'}
          {jaCategorizados > 0 && (
            <span className="text-gray-500">
              {' '}
              · {jaCategorizados} já {jaCategorizados === 1 ? 'tem' : 'têm'} categoria e{' '}
              {jaCategorizados === 1 ? 'será sobrescrito' : 'serão sobrescritos'}
            </span>
          )}
        </div>

        <div>
          <Input
            label="Categoria"
            list="categorias-lote"
            placeholder="Ex.: Bebidas"
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
          />
          <datalist id="categorias-lote">
            {categorias.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
          <p className="mt-1 text-xs text-gray-500">
            Deixe em branco para <strong>remover</strong> a categoria dos selecionados.
          </p>
        </div>

        {categorias.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {categorias.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCategoria(c)}
                className={`rounded-full px-3 py-1 text-sm transition-colors ${
                  categoria === c
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {mutation.isError && (
          <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{mutation.error.message}</span>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant={limpando ? 'danger' : 'primary'}
            isLoading={mutation.isPending}
            onClick={() => mutation.mutate({ produtoIds: produtos.map((p) => p.id), categoria })}
          >
            <Tag className="mr-2 h-4 w-4" />
            {limpando ? 'Remover categoria' : 'Aplicar'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
